import {
  type TrendingListItemResponse,
  type TrendingListItemResponseTrendingListResponseRelatedPagedData,
} from '@on3/api';
import { Brands } from '@on3/ui-lib/components/Brands';
import { Tag } from '@on3/ui-lib/components/Tag';
import { useSite } from '@on3/ui-lib/index';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import Link from 'next/link';
import { useMemo, useState } from 'react';

import styles from './TopHeadlines.module.scss';

export const TopHeadlines = ({
  trendingList,
}: {
  trendingList:
    | TrendingListItemResponseTrendingListResponseRelatedPagedData[]
    | TrendingListItemResponseTrendingListResponseRelatedPagedData;
}) => {
  const { formatHref } = useDynamicRoute();
  const { currentSite } = useSite();
  const [activeList, setActiveList] = useState<number>(0);

  const data = useMemo(
    () =>
      Array.isArray(trendingList) ? trendingList[activeList] : trendingList,
    [activeList, trendingList],
  );

  const list = useMemo(() => data?.list ?? [], [data]);

  const showLink = useMemo(
    () =>
      (data?.pagination?.limit ?? 0) <
      Math.min(Number(data?.pagination?.count) || 10, 10),
    [data],
  );

  // @NOTE: this logic is flawed and will need to be revisited if trending lists aren't
  // always tied to a site or if we want to link to something other than the site homepage
  const viewAllPath = useMemo(() => {
    if (data.relatedModel?.key === 1) return '/';

    return currentSite.url || '/';
  }, [data, currentSite.url]);

  const showToggles = Array.isArray(trendingList) && trendingList.length > 1;

  return (
    <section
      aria-labelledby="top-headlines"
      className={styles.block}
      data-active-list={activeList}
      role="complementary"
    >
      {showToggles && (
        <nav className={styles.toggles}>
          {trendingList.map((item, index) => (
            <button
              aria-pressed={activeList === index}
              className={styles.toggle}
              key={item.relatedModel?.key}
              onClick={() => setActiveList(index)}
            >
              {item.relatedModel?.name ?? 'Unknown'}
            </button>
          ))}
        </nav>
      )}
      <h2 className={styles.title} id="top-headlines">
        {data?.relatedModel?.displayName ?? data?.relatedModel?.name}
        {data?.relatedModel?.key === 1 && (
          <Brands className={styles.logos} variant="small" />
        )}
      </h2>
      <ol aria-live="polite" className={styles.list}>
        {list.map((item: TrendingListItemResponse, index) => (
          <li key={item.key}>
            <Link
              className={styles.listItem}
              href={item.url ?? ''}
              title={item.headline ?? ''}
            >
              <span className={styles.index}>{index + 1}</span>
              <div className={styles.content}>
                <div>
                  <h3>{item.subject}</h3>
                  <p>{item.headline}</p>
                </div>
                <Tag type={item.type} />
              </div>
            </Link>
          </li>
        ))}
      </ol>

      {showLink && (
        <Link
          className={styles.viewAll}
          href={formatHref({
            path: viewAllPath,
          })}
        >
          View All
        </Link>
      )}
    </section>
  );
};
